import type {
  BillboardApi,
  BillboardCreativeInput,
  BillboardPlacementStatus,
  BillboardQuote,
  BillboardReportInput,
  BillboardSlot,
  BillboardSlotsResponse,
  BillboardStatusResponse,
} from "./BillboardApi";

type MockPlacementRecord = {
  quote: BillboardQuote;
  input: BillboardCreativeInput;
  status: BillboardPlacementStatus;
  activatedAt: string;
};

export class MockBillboardApi implements BillboardApi {
  private readonly startedAt = Date.now();
  private readonly placements = new Map<string, MockPlacementRecord>();
  private slots: BillboardSlot[] = [
    this.slot("hourly", 1_200, {
      id: "mock-house-hourly",
      message: "MERGE. HONK.\nREPEAT.",
      destinationUrl: null,
      activatedAt: new Date().toISOString(),
    }),
    this.slot("daily", 4_900, null),
    this.slot("forever", 19_900, null),
  ];

  public getSlots(): Promise<BillboardSlotsResponse> {
    return Promise.resolve({
      enabled: true,
      serverTime: new Date().toISOString(),
      slots: structuredClone(this.slots),
    });
  }

  public createPaymentIntent(
    input: BillboardCreativeInput,
  ): Promise<BillboardQuote> {
    const slot = this.slots.find((candidate) => candidate.id === input.slotId);
    if (!slot) {
      return Promise.reject(new Error("That billboard is unavailable."));
    }

    const placementId = crypto.randomUUID();
    const activatedAt = new Date().toISOString();
    const quote: BillboardQuote = {
      placementId,
      clientSecret: `mock_${placementId}`,
      amountCents: this.currentPrice(slot),
      currency: "usd",
      slotVersion: slot.version,
      quoteExpiresAt: new Date(Date.now() + 10 * 60_000).toISOString(),
    };
    this.placements.set(placementId, {
      quote,
      input,
      status: "active",
      activatedAt,
    });
    this.slots = this.slots.map((candidate) =>
      candidate.id === input.slotId
        ? {
            ...candidate,
            anchorPriceCents: quote.amountCents + 100,
            anchorAt: activatedAt,
            currentPriceCents: quote.amountCents + 100,
            version: candidate.version + 1,
            placement: {
              id: placementId,
              message: input.message,
              destinationUrl: input.destinationUrl
                ? input.destinationUrl.trim()
                : null,
              activatedAt,
            },
          }
        : candidate,
    );
    return Promise.resolve(quote);
  }

  public getStatus(placementId: string): Promise<BillboardStatusResponse> {
    const record = this.placements.get(placementId);
    if (!record) {
      return Promise.reject(new Error("That mock placement is unavailable."));
    }
    return Promise.resolve({
      placementId,
      slotId: record.input.slotId,
      status: record.status,
      paymentStatus: "succeeded",
      amountCents: record.quote.amountCents,
      currency: record.quote.currency,
      quoteExpiresAt: record.quote.quoteExpiresAt,
      activatedAt: record.activatedAt,
      failureReason: null,
    });
  }

  public report(_input: BillboardReportInput): Promise<{ accepted: boolean }> {
    return Promise.resolve({ accepted: true });
  }

  private slot(
    id: BillboardSlot["id"],
    anchorPriceCents: number,
    placement: BillboardSlot["placement"],
  ): BillboardSlot {
    return {
      id,
      anchorPriceCents,
      anchorAt: new Date(this.startedAt).toISOString(),
      currentPriceCents: anchorPriceCents,
      version: 1,
      placement,
    };
  }

  private currentPrice(slot: BillboardSlot): number {
    if (slot.id === "forever") return slot.anchorPriceCents;
    const interval = slot.id === "hourly" ? 3_600_000 : 86_400_000;
    const elapsed = Math.floor(
      Math.max(0, Date.now() - new Date(slot.anchorAt).getTime()) / interval,
    );
    return Math.max(100, slot.anchorPriceCents - elapsed * 100);
  }
}
